import React, { useState, useMemo, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useAniList } from '../context/AniListContext';
import { getRelationTypeDisplay, formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { groupPendingAnimeByRelation, sortPendingGroups } from '../services/discoverGroupingService';
import { getRemovedAnimeIds, removeRemovedAnime, initRemovedAnimeIds } from '../services/removedAnimeService';
import { getAnimeByIds } from '../services/apiService';
import './RemovedAnimes.css';
import './NewRelatedAnimeGrouped.css';
import './Search.css'; // Reuse modal styles from Search

const RemovedAnimes = ({ onRefresh, showGrouped, setShowGrouped }) => {
    const { pendingAnime, animeGroups, isProcessingGroups, userData } = useAniList();
    const { titlePreference } = useTitlePreference();
    const [filterRelationType, setFilterRelationType] = useState('ALL');
    const [successMessage, setSuccessMessage] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [selectedAnime, setSelectedAnime] = useState(new Set());
    const [selectedGroups, setSelectedGroups] = useState(new Set());
    const [restoreCounter, setRestoreCounter] = useState(0);
    const [detailAnime, setDetailAnime] = useState(null); // For detail overlay modal
    const [catalogFallback, setCatalogFallback] = useState([]); // Fallback data from server catalog
    const [loadingCatalog, setLoadingCatalog] = useState(false); // Loading state for catalog fallback

    // Initialize removed anime IDs from database on mount (triggers migration + server sync)
    useEffect(() => {
        initRemovedAnimeIds().then(() => {
            setRestoreCounter(c => c + 1); // Trigger re-render with cached IDs
        });
    }, []);

    // Prevent body scroll when detail modal is open
    useEffect(() => {
        if (detailAnime) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [detailAnime]);

    const formatAnimeInfo = (anime) => {
        const STATUS_MAP = { 'FINISHED': 'Finished', 'RELEASING': 'Airing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' };
        const parts = [];
        if (anime.format) parts.push(anime.format);
        if (anime.episodes) parts.push(`${anime.episodes} eps`);
        if (anime.status) parts.push(STATUS_MAP[anime.status] || anime.status);
        return parts.join(' • ');
    };

    // Get removed anime IDs - now depends on restoreCounter for immediate UI updates
    const removedIds = useMemo(() => getRemovedAnimeIds(), [restoreCounter]);

    // Filter to show only removed anime from pendingAnime
    const removedFromPending = useMemo(() => {
        return pendingAnime.filter(anime => removedIds.has(anime.id));
    }, [pendingAnime, removedIds]);

    // Find removed IDs that are NOT in pendingAnime (missing from group build)
    const missingIds = useMemo(() => {
        const pendingIds = new Set(removedFromPending.map(a => a.id));
        // Also exclude IDs that are in user's anime list (stale entries)
        const userAnimeIds = new Set();
        if (userData?.lists) {
            userData.lists.forEach(list => {
                (list.entries || []).forEach(e => {
                    if (e.media?.id) userAnimeIds.add(e.media.id);
                });
            });
        }
        return [...removedIds].filter(id => !pendingIds.has(id) && !userAnimeIds.has(id));
    }, [removedFromPending, removedIds, userData]);

    // Fetch missing removed anime from server catalog
    useEffect(() => {
        if (missingIds.length > 0 && catalogFallback.length === 0 && !loadingCatalog) {
            console.log(`🔍 Fetching ${missingIds.length} missing removed anime from server catalog...`);
            setLoadingCatalog(true);
            getAnimeByIds(missingIds).then(data => {
                if (data && data.length > 0) {
                    const enriched = data.filter(Boolean).map(anime => ({
                        ...anime,
                        relationType: anime.relationType || 'RELATED',
                        startDate: anime.startDate || (anime.seasonYear ? {
                            year: anime.seasonYear,
                            month: { WINTER: 1, SPRING: 4, SUMMER: 7, FALL: 10 }[anime.season] || null,
                            day: null
                        } : null)
                    }));
                    setCatalogFallback(enriched);
                    console.log(`✅ Loaded ${enriched.length} missing removed anime from catalog`);
                }
            }).catch(err => {
                console.warn('⚠️ Could not fetch missing removed anime:', err.message);
            }).finally(() => {
                setLoadingCatalog(false);
            });
        }
    }, [missingIds, catalogFallback.length, loadingCatalog]);

    // Merge: pendingAnime matches + catalog fallback for missing entries
    const removedAnime = useMemo(() => {
        if (catalogFallback.length === 0) return removedFromPending;
        // Merge without duplicates
        const ids = new Set(removedFromPending.map(a => a.id));
        const merged = [...removedFromPending];
        for (const anime of catalogFallback) {
            if (!ids.has(anime.id)) merged.push(anime);
        }
        return merged;
    }, [removedFromPending, catalogFallback]);

    // Filter removed anime (flat view)
    const filteredRemoved = useMemo(() => {
        let filtered = [...removedAnime];

        // Filter by relation type
        if (filterRelationType !== 'ALL') {
            filtered = filtered.filter(anime => anime.relationType === filterRelationType);
        }

        return filtered;
    }, [removedAnime, filterRelationType]);

    // Grouped view data
    const removedGroups = useMemo(() => {
        if (!showGrouped) return [];

        let groups = groupPendingAnimeByRelation(removedAnime, animeGroups);

        // Apply filters to each group's anime
        if (filterRelationType !== 'ALL') {
            groups = groups.map(group => {
                let filtered = [...group.pendingAnime];

                // Filter by relation type
                if (filterRelationType !== 'ALL') {
                    filtered = filtered.filter(anime => anime.relationType === filterRelationType);
                }

                return {
                    ...group,
                    pendingAnime: filtered,
                    stats: { ...group.stats, count: filtered.length }
                };
            }).filter(group => group.pendingAnime.length > 0);
        }

        // Sort groups by name (default)
        return sortPendingGroups(groups, 'name', 'asc');
    }, [removedAnime, animeGroups, showGrouped, filterRelationType]);

    // Get unique relation types for filter
    const relationTypes = useMemo(() => {
        const types = new Set(removedAnime.map(anime => anime.relationType));
        return ['ALL', ...Array.from(types)];
    }, [removedAnime]);

    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    const toggleSelectAnime = (animeId) => {
        const newSelected = new Set(selectedAnime);
        if (newSelected.has(animeId)) {
            newSelected.delete(animeId);
        } else {
            newSelected.add(animeId);
        }
        setSelectedAnime(newSelected);
    };

    const toggleSelectGroup = (groupId, group) => {
        const newSelected = new Set(selectedAnime);
        const groupAnimeIds = group.pendingAnime.map(a => a.id);
        const allSelected = groupAnimeIds.every(id => selectedAnime.has(id));

        if (allSelected) {
            groupAnimeIds.forEach(id => newSelected.delete(id));
            selectedGroups.delete(groupId);
        } else {
            groupAnimeIds.forEach(id => newSelected.add(id));
            selectedGroups.add(groupId);
        }

        setSelectedAnime(newSelected);
        setSelectedGroups(new Set(selectedGroups));
    };

    const selectAll = () => {
        const allIds = new Set(filteredRemoved.map(anime => anime.id));
        setSelectedAnime(allIds);
    };

    const deselectAll = () => {
        setSelectedAnime(new Set());
    };

    const restoreSelectedAnime = async () => {
        if (selectedAnime.size === 0) {
            alert('No anime selected');
            return;
        }

        let successCount = 0;
        for (const animeId of selectedAnime) {
            if (await removeRemovedAnime(animeId)) successCount++;
        }

        if (successCount > 0) {
            setSuccessMessage(`✅ Restored ${successCount} selected anime!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setSelectedAnime(new Set());
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRestoreCounter(prev => prev + 1);
            });
        }
    };

    const restoreSingleAnime = async (animeId) => {
        const success = await removeRemovedAnime(animeId);
        if (success) {
            setSuccessMessage('Anime restored successfully! ✅');
            setTimeout(() => setSuccessMessage(''), 3000);
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRestoreCounter(prev => prev + 1);
            });
        } else {
            alert('Failed to restore anime');
        }
    };

    const restoreAllInGroup = async (group) => {
        if (group.pendingAnime.length === 0) return;

        let successCount = 0;
        for (const anime of group.pendingAnime) {
            const success = await removeRemovedAnime(anime.id);
            if (success) successCount++;
        }

        if (successCount > 0) {
            setSuccessMessage(`✅ Restored ${successCount} anime from ${group.name}!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRestoreCounter(prev => prev + 1);
            });
        }
    };

    if (isProcessingGroups) {
        return (
            <div className="discover-container">
                <div className="loading-state">
                    <div className="loader"></div>
                    <p>Processing groups...</p>
                    <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '8px' }}>
                        💡 If progress appears stuck, don't worry - it's still working in the background
                    </p>
                </div>
            </div>
        );
    }

    if (!removedAnime || removedAnime.length === 0) {
        return (
            <div className="discover-container">
                <div className="empty-state">
                    <div className="empty-icon">🗑️</div>
                    <h3>No Removed Anime</h3>
                    <p>You haven't removed any anime yet.</p>
                    <p>Use the "Remove" button in the New Related tab to hide anime you're not interested in.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="discover-container removed-anime-container">
            {/* Header */}
            <div className="discover-header">
                <div className="header-content">
                    <h1>🗑️ Removed Anime</h1>
                    <p>Found {removedAnime.length} removed anime</p>
                </div>
                {/* View Toggle */}
                <button
                    onClick={() => setShowGrouped(!showGrouped)}
                    className="view-toggle-btn"
                    title={showGrouped ? 'Switch to Flat View' : 'Switch to Grouped View'}
                >
                    {showGrouped ? '📋 Flat View' : '📚 Grouped View'}
                </button>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="success-banner">
                    {successMessage}
                </div>
            )}

            {/* Toolbar */}
            <div className="discover-toolbar">
                {/* Relation Type Filter */}
                <div className="filter-group">
                    <label>Filter by Relation:</label>
                    <select
                        value={filterRelationType}
                        onChange={(e) => setFilterRelationType(e.target.value)}
                    >
                        {relationTypes.map(type => (
                            <option key={type} value={type}>
                                {type === 'ALL' ? 'All Types' : getRelationTypeDisplay(type)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Quick Actions - for grouped view: select all anime */}
            {showGrouped && removedGroups.length > 0 && (
                <div className="quick-actions">
                    <button onClick={selectedAnime.size > 0 ? deselectAll : selectAll}>
                        {selectedAnime.size > 0 ? '❌ Deselect All' : `✅ Select All (${filteredRemoved.length})`}
                    </button>
                </div>
            )}

            {/* Quick Actions - only in flat view */}
            {!showGrouped && filteredRemoved.length > 0 && (
                <div className="quick-actions">
                    <button onClick={selectedAnime.size > 0 ? deselectAll : selectAll}>
                        {selectedAnime.size > 0 ? '❌ Deselect All' : `✅ Select All (${filteredRemoved.length})`}
                    </button>
                </div>
            )}

            {/* Selection Actions */}
            {selectedAnime.size > 0 && (
                <div className="selection-bar">
                    <span>{selectedAnime.size} selected</span>
                    <div className="selection-actions">
                        <button
                            onClick={restoreSelectedAnime}
                            className="restore-selected-btn"
                        >
                            ♻️ Restore Selected ({selectedAnime.size})
                        </button>
                    </div>
                </div>
            )}

            {/* Grouped View */}
            {showGrouped ? (
                <div className="pending-groups-container">
                    {removedGroups.length === 0 ? (
                        <div className="empty-state">
                            <p>No anime found matching your filters.</p>
                        </div>
                    ) : (
                        removedGroups.map(group => {
                            const isExpanded = expandedGroups.has(group.id);

                            return (
                                <div key={group.id} className={`pending-group-card ${isExpanded ? 'expanded' : ''} ${group.pendingAnime.every(a => selectedAnime.has(a.id)) ? 'group-selected' : ''}`}>
                                    {/* Group Header */}
                                    <div className="pending-group-header">
                                        {/* Group Selection Checkbox */}
                                        <div className="group-selection-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={group.pendingAnime.every(a => selectedAnime.has(a.id))}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    toggleSelectGroup(group.id, group);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="group-header-main" onClick={() => toggleGroup(group.id)}>
                                            <div className="group-title-section">
                                                <h2 className="group-name">
                                                    {isExpanded ? '📖' : '📚'} {group.name}
                                                </h2>
                                                <div className="group-stats-mini">
                                                    <span className="stat-count">{group.stats.count} anime</span>
                                                    {group.stats.averageScore > 0 && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="stat-score">⭐ {(group.stats.averageScore / 10).toFixed(1)}</span>
                                                        </>
                                                    )}
                                                    {group.stats.totalEpisodes > 0 && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="stat-episodes">🎬 {group.stats.totalEpisodes} eps</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <button className="expand-btn" title={isExpanded ? 'Collapse' : 'Expand'}>
                                                {isExpanded ? '▼' : '▶'}
                                            </button>
                                        </div>

                                        {/* Group Actions */}
                                        <div className="group-actions">
                                            <button
                                                onClick={() => restoreAllInGroup(group)}
                                                className="group-action-btn restore-group-btn"
                                                title="Restore all anime in this group"
                                            >
                                                ♻️ Restore All ({group.stats.count})
                                            </button>
                                        </div>
                                    </div>

                                    {/* Group Content (Expanded) */}
                                    {isExpanded && (
                                        <div className="pending-group-content">
                                            {group.pendingAnime.map(anime => {
                                                const title = getDisplayTitle(anime, titlePreference);
                                                const releaseDate = formatReleaseDate(anime.startDate);

                                                return (
                                                    <div
                                                        key={anime.id}
                                                        className={`pending-card removed-card ${selectedAnime.has(anime.id) ? 'selected' : ''}`}
                                                        onClick={() => setDetailAnime(anime)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {/* Selection Checkbox */}
                                                        <div className="anime-selection-checkbox" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedAnime.has(anime.id)}
                                                                onChange={() => toggleSelectAnime(anime.id)}
                                                            />
                                                        </div>
                                                        {/* Cover Image */}
                                                        <div className="anime-cover-container">
                                                            <img
                                                                src={anime.coverImage?.large || anime.coverImage?.medium}
                                                                alt={title}
                                                                className="anime-cover"
                                                            />
                                                        </div>

                                                        {/* Anime Info */}
                                                        <div className="anime-info">
                                                            <h3 className="anime-title">
                                                                <a
                                                                    href={`https://anilist.co/anime/${anime.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {title}
                                                                </a>
                                                            </h3>

                                                            {/* Relation Info */}
                                                            <div className="relation-info">
                                                                <span className="relation-badge">
                                                                    {getRelationTypeDisplay(anime.relationType)}
                                                                </span>
                                                                {anime.relatedTo && (
                                                                    <span className="related-to">
                                                                        → {anime.relatedTo.title}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Meta */}
                                                            <div className="anime-meta">
                                                                <span>📅 {releaseDate}</span>
                                                                {anime.episodes && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>🎬 {anime.episodes} eps</span>
                                                                    </>
                                                                )}
                                                                {anime.averageScore && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>⭐ {(anime.averageScore / 10).toFixed(1)}</span>
                                                                    </>
                                                                )}
                                                                {anime.format && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{anime.format}</span>
                                                                    </>
                                                                )}
                                                                {anime.status && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{({ 'FINISHED': 'Finished', 'RELEASING': 'Airing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' })[anime.status] || anime.status}</span>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {/* Description (truncated) */}
                                                            {anime.description && (
                                                                <p className="anime-description">
                                                                    {anime.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                                </p>
                                                            )}

                                                            {/* Restore Button */}
                                                            <div className="anime-actions" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    onClick={() => restoreSingleAnime(anime.id)}
                                                                    className="restore-btn"
                                                                >
                                                                    ♻️ Restore to New Related
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                /* Flat View */
                <div className="pending-list">
                    {filteredRemoved.length === 0 ? (
                        <div className="empty-state">
                            <p>No anime found matching your filters.</p>
                        </div>
                    ) : (
                        filteredRemoved.map(anime => {
                            const title = getDisplayTitle(anime, titlePreference);
                            const releaseDate = formatReleaseDate(anime.startDate);

                            return (
                                <div
                                    key={anime.id}
                                    className={`pending-card removed-card ${selectedAnime.has(anime.id) ? 'selected' : ''}`}
                                    onClick={() => setDetailAnime(anime)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Selection Checkbox */}
                                    <div className="anime-selection-checkbox" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedAnime.has(anime.id)}
                                            onChange={() => toggleSelectAnime(anime.id)}
                                        />
                                    </div>
                                    {/* Cover Image */}
                                    <div className="anime-cover-container">
                                        <img
                                            src={anime.coverImage?.large || anime.coverImage?.medium}
                                            alt={title}
                                            className="anime-cover"
                                        />
                                    </div>

                                    {/* Anime Info */}
                                    <div className="anime-info">
                                        <h3 className="anime-title">
                                            <a
                                                href={`https://anilist.co/anime/${anime.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {title}
                                            </a>
                                        </h3>

                                        {/* Relation Info */}
                                        <div className="relation-info">
                                            <span className="relation-badge">
                                                {getRelationTypeDisplay(anime.relationType)}
                                            </span>
                                            {anime.relatedTo && (
                                                <span className="related-to">
                                                    → {anime.relatedTo.title}
                                                </span>
                                            )}
                                        </div>

                                        {/* Meta */}
                                        <div className="anime-meta">
                                            <span>📅 {releaseDate}</span>
                                            {anime.episodes && (
                                                <>
                                                    <span>•</span>
                                                    <span>🎬 {anime.episodes} eps</span>
                                                </>
                                            )}
                                            {anime.averageScore && (
                                                <>
                                                    <span>•</span>
                                                    <span>⭐ {(anime.averageScore / 10).toFixed(1)}</span>
                                                </>
                                            )}
                                            {anime.format && (
                                                <>
                                                    <span>•</span>
                                                    <span>{anime.format}</span>
                                                </>
                                            )}
                                            {anime.status && (
                                                <>
                                                    <span>•</span>
                                                    <span>{({ 'FINISHED': 'Finished', 'RELEASING': 'Airing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' })[anime.status] || anime.status}</span>
                                                </>
                                            )}
                                        </div>

                                        {/* Description (truncated) */}
                                        {anime.description && (
                                            <p className="anime-description">
                                                {anime.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                            </p>
                                        )}

                                        {/* Restore Button */}
                                        <div className="anime-actions" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => restoreSingleAnime(anime.id)}
                                                className="restore-btn"
                                            >
                                                ♻️ Restore to New Related
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Summary Footer */}
            <div className="discover-summary">
                {showGrouped ? (
                    <p>
                        Showing {removedGroups.length} group{removedGroups.length !== 1 ? 's' : ''} with{' '}
                        {removedGroups.reduce((sum, g) => sum + g.stats.count, 0)} of {removedAnime.length} removed anime
                    </p>
                ) : (
                    <p>
                        Showing {filteredRemoved.length} of {removedAnime.length} removed anime
                    </p>
                )}
            </div>

            {/* Anime Detail Overlay Modal (same as Discover) */}
            {detailAnime && (
                <div className="anime-modal-overlay" onClick={() => setDetailAnime(null)}>
                    <div className="anime-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close-btn"
                            onClick={() => setDetailAnime(null)}
                        >
                            ✕
                        </button>

                        {detailAnime.bannerImage && (
                            <img
                                src={detailAnime.bannerImage}
                                alt=""
                                className="anime-banner"
                            />
                        )}

                        <div className="modal-content">
                            <div className="modal-header">
                                {detailAnime.coverImage?.large && (
                                    <img
                                        src={detailAnime.coverImage.large}
                                        alt={getDisplayTitle(detailAnime, titlePreference)}
                                        className="modal-cover"
                                    />
                                )}
                                <div className="modal-header-info">
                                    <h2>
                                        <a
                                            href={`https://anilist.co/anime/${detailAnime.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {getDisplayTitle(detailAnime, titlePreference)}
                                        </a>
                                    </h2>
                                    {detailAnime.title?.english && detailAnime.title.english !== detailAnime.title?.romaji && (
                                        <p className="alt-title">{detailAnime.title.english}</p>
                                    )}
                                    <p className="anime-meta">{formatAnimeInfo(detailAnime)}</p>
                                    {detailAnime.averageScore && (
                                        <div className="score-large">
                                            ⭐ {detailAnime.averageScore / 10}/10
                                        </div>
                                    )}
                                    {detailAnime.relationType && (
                                        <div className="relation-info" style={{ marginTop: '0.5rem' }}>
                                            <span className="relation-badge">
                                                {getRelationTypeDisplay(detailAnime.relationType)}
                                            </span>
                                            {detailAnime.relatedTo && (
                                                <span className="related-to">→ {detailAnime.relatedTo.title}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {detailAnime.description && (
                                <div className="modal-description">
                                    <h3>Description</h3>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: detailAnime.description
                                        }}
                                    />
                                </div>
                            )}

                            {detailAnime.genres && detailAnime.genres.length > 0 && (
                                <div className="modal-genres">
                                    <h3>Genres</h3>
                                    <div className="genre-list">
                                        {detailAnime.genres.map((genre, idx) => (
                                            <span key={idx} className="genre-tag-large">
                                                {genre}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                <a
                                    href={`https://anilist.co/anime/${detailAnime.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-anilist"
                                >
                                    View on AniList
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RemovedAnimes;
